import React, { useState, useEffect, useRef } from 'react';
import { useSafeTranslation } from '../hooks/useSafeTranslation';
import { Globe, ChevronDown } from 'lucide-react';
import { applyDirection } from '../utils/rtlUtils';
import posApi from '../utils/posApi';

const LanguageSwitcher = ({ variant = 'default', showLabel = true }) => {
  const { i18n } = useSafeTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  const languages = [
    { code: 'en', name: 'English', nativeName: 'English', flag: '🇬🇧' },
    { code: 'ar', name: 'Arabic', nativeName: 'العربية', flag: '🇸🇦' }
  ];

  const currentLanguage = languages.find(lang => lang.code === i18n.language) || languages[0];

  const changeLanguage = async (lng) => {
    setIsOpen(false);
    
    // Update i18n and localStorage immediately
    i18n.changeLanguage(lng);
    applyDirection(lng);
    localStorage.setItem('language', lng);
    
    console.log(`🌐 Language changed to: ${lng}`);
    
    // Save to backend if user is logged in (await to ensure it completes)
    const posToken = localStorage.getItem('pos_token');
    if (posToken) {
      try {
        await posApi.post('/api/pos/user/preferences', {
          preferences: { language: lng }
        });
        console.log('✅ Language preference saved to backend');
      } catch (error) {
        console.error('❌ Failed to save language preference:', error);
      }
    }
    
    // Reload page AFTER backend save completes to ensure persistence
    // This prevents race condition where old language gets reloaded
    window.location.reload();
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Variant styles
  const getButtonStyles = () => {
    switch (variant) {
      case 'ghost':
        return 'text-gray-300 hover:text-white hover:bg-gray-800/50 border-transparent';
      case 'outline':
        return 'text-white border-gray-600 hover:bg-gray-800/50';
      default:
        return 'text-gray-300 hover:text-white hover:bg-white/10 border-transparent';
    }
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Language Selector Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center gap-2 px-3 py-2 rounded-lg border transition-colors ${getButtonStyles()}`}
        aria-label="Select Language"
      >
        <Globe className="w-4 h-4" />
        {showLabel && (
          <>
            <span className="text-sm font-medium hidden sm:inline">
              {currentLanguage.nativeName}
            </span>
            <span className="text-sm font-medium sm:hidden">
              {currentLanguage.code.toUpperCase()}
            </span>
          </>
        )}
        <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className="absolute right-0 mt-2 w-48 bg-gray-800 border border-gray-700 rounded-lg shadow-xl z-50 overflow-hidden">
          {languages.map((lang) => (
            <button
              key={lang.code}
              onClick={() => changeLanguage(lang.code)}
              className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors ${
                currentLanguage.code === lang.code
                  ? 'bg-purple-600/20 text-purple-400'
                  : 'text-gray-300 hover:bg-gray-700'
              }`}
            >
              <span className="text-2xl">{lang.flag}</span>
              <div className="flex-1">
                <div className="text-sm font-medium">{lang.nativeName}</div>
                <div className="text-xs text-gray-500">{lang.name}</div>
              </div>
              {currentLanguage.code === lang.code && (
                <div className="w-2 h-2 rounded-full bg-purple-500"></div>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default LanguageSwitcher;
